/**
 * feedWave — THE FEED WAVE client helpers (the rail renders what the world
 * declares; inbox-is-work wave).
 *
 * The rail's tab strip is the SERVED feed row (`query {tab:"feed"}` —
 * feed.tab tokens in the human's own order); the inbox is the human's own
 * inbox row (kind/open/list/seen). The write hooks are plain message
 * announces on the EXISTING channel path whose ref lane declares ONE
 * feed/inbox atom with the declared evidence data — the world's feed writer
 * owns the keyed claims (idempotent, byte-identical replay).
 *
 * Lanes here: feed:left tab rearrange (sci//feed:left:@tab**), inbox seen
 * (sci//inbox:seen:@ref**), the LEFT overlay lanes (the confirm-loop wave:
 * sci//feed:@binary:@inbox XOR sci//feed:@binary:@tab** — the rail shows
 * nothing, the inbox, or the tab strip, never both; the shared
 * feed:left:@binary is DEAD), the right rail binary (sci//feed:right:@binary),
 * the middle current tab (sci//feed:middle:@(tab:current) — the artifacts
 * history region), the mode selection (sci//feed:@(ui:kind:mode):current),
 * and THE ACTIVATION (sci//artifact:activate:@activated).
 */

import { icons } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { InboxEntry, InboxEntryKind, InboxSeenEntry } from "./client";
import type { Announcement, Machine } from "./contract";
import { FEED_ROW_REF, INBOX_ITEM_REF_SETS, INBOX_ROW_REF } from "./feedRefs";

type JsonRecord = Record<string, unknown>;
const isRecord = (value: unknown): value is JsonRecord => (
  typeof value === "object" && value !== null && !Array.isArray(value)
);

export type WorkStatus = NonNullable<Machine["status"]>;

/** Waiting is exactly the oracle status face's pending states. Identity/current
 * selection never implies work, and complete/fail:* are terminal outcomes. */
export const isWaitingStatus = (status: string): status is "start" | "retry" =>
  status === "start" || status === "retry";

// ── the icon-word mapping ────────────────────────────────────────────────────
// THE CONTRACT IS BARE LUCIDE WORDS (the world serves e.g. "menu",
// "layout-template" in body icon values; ui row IDS like "icon:menu" are row
// identity, never a face value). The mapping is purely mechanical:
// kebab→Pascal, look up the lucide-react `icons` map — no hardcoded icon
// table, and NO `icon:` prefix stripping (a prefixed value is a producer
// contract violation and hits the LOUD unknown path — no compatibility
// alias). An unknown non-empty word errors LOUD (once per word) and returns
// null; every caller renders a VISIBLE fallback glyph, never silent nothing.
const unknownIconWords = new Set<string>();
export const lucideIconOfWord = (iconWord: string): LucideIcon | null => {
  const word = String(iconWord || "").trim();
  if (!word) return null;
  const pascal = word
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  const icon = (icons as Record<string, LucideIcon>)[pascal] ?? null;
  if (!icon && !unknownIconWords.has(word)) {
    unknownIconWords.add(word);
    console.error(
      `[feed] served icon word '${word}' is not a lucide icon — the contract is BARE lucide words (never 'icon:'-prefixed row ids); rendering the fallback glyph`,
    );
  }
  return icon;
};

/** A served tab TOKEN humanized for the face — mechanical word cleanup only
 * (the accessible label when the served tab face carries no label). Never a
 * protocol spelling on screen, never an `icon:` prefix strip (bare words are
 * the contract). */
export const humanizeToken = (token: string): string => {
  const clean = String(token || "").replace(/[-_]+/g, " ").trim();
  if (!clean) return "";
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

/** The humanized word for a work/machine status (the EXISTING enum — never a
 * new vocabulary). */
export const statusWordOf = (status: string): string => ({
  "start": "working",
  "complete": "done",
  "fail:start": "send failed",
  "fail:complete": "failed",
  "fail:block": "blocked",
  "retry": "retrying",
}[status] ?? status);

// ── the world write hooks (announce shapes) ──────────────────────────────────
// The write rides a stable per-user feed conversation (the harness-proven
// spelling `feed-ws-<userId>`); the world dispatches on the DECLARED ref atom,
// never the conversation.
const feedWriteConversationId = (userId: string): string =>
  `feed-ws-${userId}`.slice(0, 40);

const naiveNow = (): string => new Date().toISOString().replace(/Z$/, "");

export type FeedWriteAnnouncement = {
  type?: string;
  conversationId: string;
  timestamp: string;
  machine: Record<string, unknown>;
  position?: { path: string };
  ref: string[];
  artifact: unknown[];
  evidence: unknown[];
  confirmation: unknown[];
};

export type LifecycleDeactivationTarget = {
  kind: "template" | "conversation" | "agent" | "entity";
  address: string;
  machine: string;
  scopePath: string;
};

const DEACTIVATION_ATOM_BY_KIND: Record<LifecycleDeactivationTarget["kind"], string> = {
  template: "sci//template:@machine:@binary",
  conversation: "sci//conversation:@machine:@binary",
  agent: "sci//agent:@machine:@binary",
  entity: "sci//entity:@machine:@binary",
};
const BINARY_FALSE_ATOM = "sci//binary:false:@(primitive:string)";

const feedWriteAnnounce = (
  userId: string,
  refAtom: string,
  data: JsonRecord,
  label: string,
): FeedWriteAnnouncement => {
  const id = `feed-write-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  return {
    conversationId: feedWriteConversationId(userId),
    timestamp: naiveNow(),
    machine: { name: "message", id, label, status: "start" },
    ref: [refAtom],
    artifact: [{
      machine: { name: "message", id, label, tags: ["Message", "Feed"], status: "start" },
      suffix: "md",
      position: "match",
      input: { text: label },
      params: [],
    }],
    evidence: [{ kind: "evidence", confidence: 1, body: { data } }],
    confirmation: [],
  };
};

/** Re-arrange: the full tab-token order (`sci//feed:left:@tab**` + {tab:[...]})
 * — the world claims the human-confidence arrangement map. */
export const rearrangeTabsAnnounce = (userId: string, order: string[]): FeedWriteAnnouncement =>
  feedWriteAnnounce(userId, "sci//feed:left:@tab**", { tab: order }, "Rearrange tabs");

/** Seen: ONE inbox entry's ref + the client's ISO timestamp
 * (`sci//inbox:seen:@ref**` + {ref, timestamp}) — keyed per ref (union). */
export const inboxSeenAnnounce = (userId: string, ref: string, timestamp: string): FeedWriteAnnouncement =>
  feedWriteAnnounce(userId, "sci//inbox:seen:@ref**", { ref, timestamp }, "Seen");

/** A rail lane's open state (the confirm-loop wave): the LEFT side is ONE
 * overlay — `sci//feed:@binary:@inbox` XOR `sci//feed:@binary:@tab**`
 * (opening one closes the other world-side; the shared feed:left:@binary is
 * DEAD); the RIGHT side keeps its own binary (`sci//feed:right:@binary`).
 * Evidence data: {open:boolean}. */
export const feedRailAnnounce = (userId: string, lane: "inbox" | "tab" | "right", open: boolean): FeedWriteAnnouncement =>
  feedWriteAnnounce(
    userId,
    lane === "inbox" ? "sci//feed:@binary:@inbox"
      : lane === "tab" ? "sci//feed:@binary:@tab**"
      : "sci//feed:right:@binary",
    { open },
    open ? `Open ${lane} rail` : `Close ${lane} rail`,
  );

/** The middle current tab (`sci//feed:middle:@(tab:current)` + {tab}) — the
 * middle region shows the CURRENT tab's artifact history; the selection is
 * WORLD state like the mode. */
export const feedMiddleAnnounce = (userId: string, tab: string): FeedWriteAnnouncement =>
  feedWriteAnnounce(userId, "sci//feed:middle:@(tab:current)", { tab }, `Open ${tab}`);

/** The mode selection (`sci//feed:@(ui:kind:mode):current` + {current}) — the
 * theme is WORLD state; the served feed.current drives dark/light. */
export const modeCurrentAnnounce = (userId: string, current: string): FeedWriteAnnouncement =>
  feedWriteAnnounce(userId, "sci//feed:@(ui:kind:mode):current", { current }, `Switch to ${current}`);

type TemplateForkInput = {
  /** sci//template:ref:@ref** — the registered source artifact address */
  ref: string;
  /** sci//template:primitive:@primitive** — the human card label */
  primitive: string;
  /** sci//template:preview:@ref** — the declared open/preview artifact */
  preview: string;
  /** exactly one declared template kind atom */
  kind: "sci//template:kind:@entity" | "sci//template:kind:@render" | "sci//template:kind:@agent";
  /** deterministic destination selected by the kind atom */
  tab: "book" | "workspace" | "agent";
};

/** The first-class template fork crossing. The correlation conversation id is
 * client-owned and unique, so the resulting conversation/artifact can retain
 * the same identity end-to-end. Deliberately NO evidence data.text: a Fork
 * command is not a human chat message and must never mint a task. */
export const templateForkAnnounce = (userId: string, input: TemplateForkInput): FeedWriteAnnouncement => {
  const announcement = feedWriteAnnounce(
    userId,
    "sci//artifact:activate:@activated",
    {
      ref: [input.ref],
      primitive: [input.primitive],
      preview: [input.preview],
      kind: input.kind,
      tab: input.tab,
    },
    "Fork template",
  );
  return {
    ...announcement,
    // A fork is a transport command, not a conversation message. Explicit
    // empty is required: omitting the lane lets the outlet synthesize its
    // default message artifact, which would render "Fork template" in the
    // pristine destination conversation.
    artifact: [],
    conversationId: `template-fork-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`,
  };
};

/** Deactivate one exact served lifecycle subject. The outlet re-derives and
 * enforces the scope from auth; this position is the declared protocol face,
 * never authority supplied by the browser. */
export const lifecycleDeactivateAnnounce = (
  userId: string,
  target: LifecycleDeactivationTarget,
): FeedWriteAnnouncement => {
  const announcement = feedWriteAnnounce(
    userId,
    DEACTIVATION_ATOM_BY_KIND[target.kind],
    {
      kind: target.kind,
      address: target.address,
      machine: target.machine,
      binary: false,
    },
    `Deactivate ${target.kind}`,
  );
  return {
    ...announcement,
    type: "deactivate",
    machine: {
      name: "deactivate",
      id: `deactivate:${target.machine}`,
      label: `Deactivate ${target.kind}`,
      status: "start",
    },
    position: { path: target.scopePath },
    ref: [DEACTIVATION_ATOM_BY_KIND[target.kind], BINARY_FALSE_ATOM],
    // This is a lifecycle command, not a message artifact.
    artifact: [],
    evidence: [{
      kind: "evidence",
      confidence: 1,
      body: {
        data: {
          kind: target.kind,
          address: target.address,
          machine: target.machine,
          binary: false,
        },
      },
    }],
    confirmation: [],
  };
};

// ── inbox entry identity ─────────────────────────────────────────────────────
/** The stable seen-write ref of a served inbox entry — the world row edge. */
export const stableInboxRefOf = (entry: InboxEntry): string => String(entry.ref);

/** Whether a served inbox entry is already in the seen list (seen pairs may
 * hold the row EDGE as an int or its string spelling; match either). */
export const inboxEntrySeen = (entry: InboxEntry, seen: InboxSeenEntry[]): boolean =>
  seen.some((pair) => String(pair?.ref ?? "").trim() === String(entry.ref));

/** The world entries not yet in the seen pairs — the badge + seen-write set. */
export const unseenInboxEntries = (list: InboxEntry[], seen: InboxSeenEntry[]): InboxEntry[] =>
  list.filter((entry) => !inboxEntrySeen(entry, seen));

// ── live ws frame classification (THE one store crossing) ────────────────────
const declaredRefsOf = (a: Announcement): Set<string> => new Set([
  ...(Array.isArray(a.ref) ? a.ref : []).map((r) => String(
    isRecord(r) ? ((r as JsonRecord).path ?? (r as JsonRecord).id ?? r) : r,
  )),
  ...(Array.isArray(a.refs) ? a.refs : []).map((r) => String(r?.path ?? "")),
]);

export type FeedFrameLane = "feed-row" | "inbox-row" | InboxEntryKind | "";

/** THE lane classifier for live ws frames — the mirrored ref sets decide
 * (feedRefs INBOX_ITEM_REF_SETS + FEED_ROW_REF/INBOX_ROW_REF); never a shape
 * sniff. Consumed ONLY by feedStore.foldLiveAnnouncement. */
export const classifyFeedFrame = (a: Announcement): FeedFrameLane => {
  const declared = declaredRefsOf(a);
  if (declared.has(FEED_ROW_REF)) return "feed-row";
  if (declared.has(INBOX_ROW_REF)) return "inbox-row";
  // Each lane's ref set is a DISJUNCTION of declared faces (the writer,
  // world control/feed.py status_target_of, accretes on ANY one face);
  // lane order is the writer's precedence — notify, then status.
  for (const [kind, refSet] of Object.entries(INBOX_ITEM_REF_SETS)) {
    if (refSet.some((atom) => declared.has(atom))) return kind as InboxEntryKind;
  }
  return "";
};

/** The evidence-carried body of a feed/inbox write or row-refresh frame
 * (the ONE evidence idiom: evidence[0].body.data). */
export const evidenceDataOf = (a: Announcement): JsonRecord | null => {
  const evidence = Array.isArray(a.evidence) ? a.evidence : [];
  for (const item of evidence) {
    const body = isRecord(item) && isRecord(item.body) ? item.body : null;
    if (body && isRecord(body.data)) return body.data;
  }
  return null;
};

/** A live classified frame projected onto the inbox entry face. A served
 * entry body riding the frame's evidence wins; else the projection is the
 * frame's own row identity + humanized machine face. A frame with no numeric
 * world row edge cannot be an inbox entry — refused LOUD, never a shape guess. */
export const inboxEntryOfAnnouncement = (a: Announcement, kind: InboxEntryKind): InboxEntry | null => {
  const data = evidenceDataOf(a);
  if (data && data.ref !== undefined && Number.isFinite(Number(data.ref))) {
    return {
      ref: Number(data.ref),
      kind: (data.kind === "notify" || data.kind === "status" ? data.kind : kind),
      icon: String(data.icon ?? ""),
      label: Array.isArray(data.label) ? data.label.map(String).filter(Boolean) : [String(data.label ?? "")].filter(Boolean),
      timestamp: String(data.timestamp ?? a.timestamp ?? ""),
      ...(typeof data.conversation === "string" && data.conversation ? { conversation: data.conversation } : {}),
    };
  }
  const ref = Number(a.id);
  if (!Number.isFinite(ref)) {
    console.error(`[inbox] a live ${kind} frame carried no world row edge (id=${a.id}) — refusing the entry`);
    return null;
  }
  return {
    ref,
    kind,
    icon: "",
    label: [String(a.machine?.label || a.machine?.name || "Update"), String(a.machine?.description || "")].filter(Boolean),
    timestamp: String(a.timestamp || ""),
    ...(a.conversationId ? { conversation: a.conversationId } : {}),
  };
};
